import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { JoinActions } from './join.actions';
import { getJoinFormActiveStep } from './join.selectors';
import { exhaustMap, filter, of, switchMap, throwError, withLatestFrom } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { FormGroupValue } from '../../modules/share/form.utils';
import { getPayment } from '@aaa/emember/store-payment';
import { JoinForm, JoinPayParams } from './join.models';
import { PaymentForm } from '@aaa/emember/share/payment-form';
import { ExecuteService } from '../services/execute.service';
import { filterByClubIds } from '../utils/filter-by-club-ids';
import { DataLayerService } from '../../modules/share/services/data-layer.service';
import {
  MembershipCampanaGetCostsJoinEvent,
  MembershipCampanaGetCostsJoinPreviewEvent,
  MembershipCampanaGetCostsJoinResponse,
  MembershipCampanaLevel,
  MembershipCampanaMethod,
  MembershipCampanaOperationExecuteEvent,
} from '@aaa/interface-joinRenew-membership-membershipCampana';
import { getClearCacheSettings, getSaveCacheSettings } from '../utils/get-cache-settings';
import { getCurrentTimeStamp } from '../utils/get-current-timestamp';
import { ValidateSucceededResponseObject } from '@aaa/emember/store-types';
import { CampanaType } from '../campana.type';
import {
  buildFreedompayPaymentEvent,
  isJoinPrimaryReal,
  toCampanaDate,
  getCampanaConfirmedMembers,
  getCampanaDiscountOffer,
  getCampanaJoinPayErrorType,
  getCampanaJoinRecostErrorType,
  getCampanaMembershipType,
  getCampanaPersonLabel,
} from '../campana-utils';
import { NotificationService } from '../../modules/share/notification/notification.service';
import { JoinFormVm } from '../../modules/join/components/join-form/join-form.vm';
import { clubOption } from '../../signals';
import { MembershipOfferItem } from '../price-offers/helpers/types';
import { PriceOffersActions } from '@aaa/emember/store-price-offers';
import { Operation, OperationExecutePayload } from '@aaa/interface-joinRenew-joinRenewLib';
import { Cybersource } from '../cybersource.type';
import { getTransactionId } from '../utils/get-transaction-id';
import { AppAnalyticsEvents, ClubApp } from '@aaa/emember/types';
import { RequestError, RequestErrorType } from '../generic-errors';
import { encryptPassword } from '../../modules/share/utils/encrypt-password';
import { checkCampanaPaymentValidation } from '../check-campana-payment-validation';

@Injectable({ providedIn: 'root' })
export class JoinCampanaSystemEffects {
  store = inject(Store);
  actions$ = inject(Actions).pipe(filterByClubIds([ClubApp.Northway]));
  executeService = inject(ExecuteService);
  dataLayer = inject(DataLayerService);
  notification = inject(NotificationService);
  joinFormVm = inject(JoinFormVm);

  updateSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JoinActions.recostValidateSucceeded),
      mergeMap(
        ({
          response: { sessionResponse: response, promotion },
        }: ValidateSucceededResponseObject<MembershipCampanaGetCostsJoinResponse>) => {
          const totalCost = response.data.dues.data.total;
          const componentOffers: MembershipOfferItem[] = [];
          const membershipType = getCampanaMembershipType(response.data.level.data.serviceLevels[0].level);

          response.data.level.data.serviceLevels.map((serviceLevel) => {
            componentOffers.push({
              offering: serviceLevel.personId === '00' ? 'primary' : 'associate',
              code: getCampanaMembershipType(serviceLevel.level),
              description: getCampanaPersonLabel(serviceLevel.personId),
              amount: serviceLevel.balance,
              selectedByDefault: true,
              conditions: [],
            });
          });

          if (response.data.dues.data.miscCharges > 0) {
            componentOffers.push({
              offering: 'optionalPrimary',
              code: 'waiveEnrollFee',
              description: 'One-Time Enrollment Fee',
              amount: response.data.dues.data.miscCharges,
              selectedByDefault: true,
              conditions: [],
            });
          }

          const discount = getCampanaDiscountOffer(response.data.dues.data.discounts, promotion);
          if (discount) {
            componentOffers.push(discount);
          }

          // miscCharges
          return [
            JoinActions.setTotalCost({ totalCost }),
            PriceOffersActions.updateAndInsert({ membershipType, componentOffers }),
          ];
        },
      ),
    ),
  );

  setConfirmedMembersSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JoinActions.paySucceeded),
      map(({ membership }: { membership: CampanaType.JoinExecuteResponseObject }) =>
        JoinActions.setConfirmedMembers({ members: getCampanaConfirmedMembers(membership?.response?.response) }),
      ),
    ),
  );

  // AXIS took the money but would not store the card for renewal. The join stands, so the member
  // has to hear it here - the confirmation page would otherwise imply auto renew is on
  warnAutoRenewNotEnrolled$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JoinActions.paySucceeded),
        filter(
          ({ membership }: { membership: CampanaType.JoinExecuteResponseObject }) =>
            !!membership?.response?.response?.arEnrollmentFailed,
        ),
        tap(() =>
          this.notification.warning(
            `Your membership is complete, but we could not set up Automatic Renewal. Please call ${clubOption().phoneNumberView} to add it.`,
            '',
            { disableTimeOut: true, closeButton: true },
          ),
        ),
      ),
    { dispatch: false },
  );

  recostValidateJoin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        JoinActions.recostValidate,
        JoinActions.updateRecostValidate,
        JoinActions.validatePromoCode,
        JoinActions.retryRecostValidate,
      ),
      // the latest form wins. exhaustMap dropped a recost while one was in flight, which was
      // harmless while every recost carried a real primary; now a landing preview can be in the
      // air when Member Information validates, and the step must not proceed on its answer
      switchMap(({ type, formValues }) =>
        of(formValues).pipe(
          switchMap((action) =>
            of(action).pipe(withLatestFrom(this.store.select(getPayment), this.store.select(getJoinFormActiveStep))),
          ),
          switchMap(([formValues, payment, activeStep]) =>
            this.recostValidation(
              formValues,
              payment,
              // Member Information's own recost and every one after it quote for real, whatever
              // isJoinPrimaryReal says: Pay needs the executionData a preview never has
              type === JoinActions.recostValidate.type || ['paymentInfo', 'summary'].includes(activeStep),
            ).pipe(
              map((res) => JoinActions.recostValidateSucceeded(res)),
              catchError((error) => of(JoinActions.recostValidateFailed({ error }))),
            ),
          ),
        ),
      ),
    ),
  );

  // the quote went stale between Summary and Pay, and nothing was charged: price the form again so
  // the member sees what AXIS will charge and can pay that
  recostAfterPriceChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JoinActions.payFailed),
      filter(({ error }) => error.type === RequestErrorType.JoinPriceChanged),
      map(() => JoinActions.updateRecostValidate({ formValues: this.joinFormVm.formGroup.value })),
    ),
  );

  // the bank refused the card when AXIS charged it, and nothing was taken: back to the card form for
  // another card. The quote and its session are kept (DECLINED-COMPLETE-PLAN.md), so Continue and Pay
  // charge the same member record instead of minting a second one
  backToPaymentAfterDecline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JoinActions.payFailed),
      filter(({ error }) => error.type === RequestErrorType.PaymentDeclined),
      map(() => JoinActions.setFormActiveStep({ activeStep: 'paymentInfo' })),
    ),
  );

  pay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JoinActions.pay),
      exhaustMap(({ params }) =>
        this.pay(params).pipe(
          map(({ membership, payment }) => JoinActions.paySucceeded({ membership, payment })),
          catchError((error) => of(JoinActions.payFailed({ error }))),
        ),
      ),
    ),
  );

  pay(params: JoinPayParams) {
    const { totalCost, executionData, selectedLevel, page, payment, formValues } = params;
    if (!executionData) {
      // no quote to execute: the last recost failed, and campana would refuse it with 4006
      return throwError(() => new RequestError(RequestErrorType.JoinFormError));
    }
    const coupon = formValues.memberInfo?.membership?.couponCode || formValues.memberInfo?.membership?.promoCode || '';
    const paymentEvent = buildFreedompayPaymentEvent(Operation.JOIN, payment.token, totalCost);

    const membershipEvent: MembershipCampanaOperationExecuteEvent = {
      executionData: executionData,
      method: MembershipCampanaMethod.OPERATION_EXECUTE,
      operation: Operation.JOIN,
    };

    const payload: OperationExecutePayload = {
      operation: Operation.JOIN,
      membershipEvent: membershipEvent,
      paymentEvent: paymentEvent,
    };

    return this.executeService
      .execute<CampanaType.JoinExecuteResponseObject, Cybersource.ExecutePaymentResponseObject>(payload)
      .pipe(
        map(({ paymentObject, validateObject, operationObject }) => {
          if (paymentObject?.meta?.isError || paymentObject?.error) {
            checkCampanaPaymentValidation(paymentObject.error || paymentObject);
          }

          if (validateObject?.error) {
            throw new RequestError(getCampanaJoinPayErrorType(validateObject.error), validateObject);
          }

          if (operationObject?.error) {
            throw new RequestError(RequestErrorType.JoinFormError, operationObject);
          }

          this.dataLayer.joinMembershipPurchaseEvent({
            transactionId: getTransactionId(paymentObject),
            context: 'ava-store ' + (page === 'join' ? AppAnalyticsEvents.JoinNew : AppAnalyticsEvents.GiftNew),
            itemMembershipLevel: selectedLevel.level,
            coupon: coupon,
            itemQuantity: 1,
            itemId: 'primary',
            itemPrice: totalCost,
            pageName: page === 'join' ? AppAnalyticsEvents.JoinNew : AppAnalyticsEvents.GiftNew,
          });

          // const membershipError =
          //   validateObject?.meta.isError || validateObject?.response.response?.attributes?.responseCode !== '000';
          //
          // if (membershipError) {
          //   throw new RequestError(RequestErrorType.MembershipError, validateObject);
          // }

          // const operationError = !!operationObject?.meta?.isError;
          // if (operationError) {
          //   checkOperationErrorsConnectSuiteSystem(operationObject.error, operationObject);
          // }

          return { membership: validateObject, payment: paymentObject };
        }),
      );
  }

  recostValidation(
    formValues: FormGroupValue<JoinForm>,
    payment: { token: string; formValues: FormGroupValue<PaymentForm> },
    quote: boolean,
  ) {
    if (!quote && !isJoinPrimaryReal(formValues)) return this.previewRecost(formValues, payment);

    const payload: MembershipCampanaGetCostsJoinEvent = {
      method: MembershipCampanaMethod.GET_COSTS_JOIN,
      cacheSettings: getClearCacheSettings(),
      primary: {
        firstName: formValues.memberInfo?.account?.firstName || '',
        lastName: formValues.memberInfo?.account?.lastName || '',
        // the join page requires a DOB for Northway (the gift page does not); '' is dropped by the
        // handler, so AXIS gets no birth date rather than a made-up one (the club keeps these records)
        birthDate: toCampanaDate(formValues.memberInfo?.account?.birthday),
        email: formValues.memberInfo?.account?.email || '',
        phone: formValues.memberInfo?.account?.phone || '',
        gender: '',
      },
      // an associate row the member has not named yet goes as '', and the handler fills it from
      // its recost-placeholder.ts, the one place those values live
      associates: (formValues.memberInfo?.membershipAssociates || []).map((associate) => {
        return {
          birthDate: toCampanaDate(associate.birthday),
          firstName: associate.firstName || '',
          lastName: associate.lastName || '',
          email: associate.email || '',
          gender: '',
          clubJoinDate: '',
          cueDate: '',
          previousMemberNumber: '',
          salutation: '',
        };
      }),
      address: {
        street: [formValues.memberInfo?.account?.address1 || '', formValues.memberInfo?.account?.address2 || ''].filter(
          Boolean,
        ),
        locality: formValues.memberInfo?.account?.city || '',
        region: formValues.memberInfo?.account?.state || '',
        postCode: formValues.memberInfo?.account?.zipcode || '',
      },
      promotionCode: formValues.memberInfo?.membership?.promoCode || '',
      couponCode: formValues.memberInfo?.membership?.couponCode || '',
      autoRenew: !!payment.formValues?.autoRenew,
      level: campanaLevelOf(formValues),
      loginCredentials: {
        encryptedPassword: encryptPassword(formValues.memberInfo?.account?.password),
      },
    };

    return this.executeService.membershipQuery<CampanaType.JoinRecostValidationResponseObject>(payload).pipe(
      map((validateObject) => {
        // A rejected recost has to stop the step. Reading the response regardless dispatched a
        // "succeeded" with no executionData, so the page kept the previous quote's total and Pay
        // sent an empty executionData (AXIS had refused the email domain - FREEDOMPAY-PLAN.md R22).
        if (validateObject.meta?.isError || !validateObject.response?.executionData) {
          throw new RequestError(getCampanaJoinRecostErrorType(validateObject.error), validateObject);
        }
        return {
          response: validateObject.response,
          executionData: validateObject.response.executionData,
        };
      }),
    );
  }

  /**
   * The price before the primary is real: the landing, a level change, a promo applied or a roster
   * edit on an empty form. Only what sets the price goes - never a name, email or password - so
   * the same inputs hash the same for every visitor and the coordinator's cache answers them from
   * one AXIS session an hour. The handler prices placeholder people and deletes the session in the
   * same request, so AXIS keeps nothing, and there is no executionData: Pay waits for the real
   * recost after Member Information.
   */
  previewRecost(
    formValues: FormGroupValue<JoinForm>,
    payment: { token: string; formValues: FormGroupValue<PaymentForm> },
  ) {
    const payload: MembershipCampanaGetCostsJoinPreviewEvent = {
      method: MembershipCampanaMethod.GET_COSTS_JOIN_PREVIEW,
      cacheSettings: getSaveCacheSettings({
        context: 'campanaJoinPreview',
        maxAge: getCurrentTimeStamp(-60 * 60 * 1000),
      }),
      level: campanaLevelOf(formValues),
      autoRenew: !!payment.formValues?.autoRenew,
      promotionCode: formValues.memberInfo?.membership?.promoCode || '',
      couponCode: formValues.memberInfo?.membership?.couponCode || '',
      postCode: formValues.memberInfo?.account?.zipcode || '',
      associateCount: (formValues.memberInfo?.membershipAssociates || []).length,
    };

    return this.executeService.membershipQuery<CampanaType.JoinRecostValidationResponseObject>(payload).pipe(
      map((validateObject) => {
        // a refused preview is a refused recost: a bad promo marks the field and prices again without it
        if (validateObject.meta?.isError || !validateObject.response) {
          throw new RequestError(getCampanaJoinRecostErrorType(validateObject.error), validateObject);
        }
        return {
          response: validateObject.response,
          executionData: '',
        };
      }),
    );
  }
}

const campanaLevelOf = (formValues: FormGroupValue<JoinForm>): MembershipCampanaLevel =>
  formValues.memberInfo?.membership?.membershipLevel
    ? (MembershipLevelMapping[
        formValues.memberInfo?.membership?.membershipLevel as keyof typeof MembershipLevelMapping
      ] as MembershipCampanaLevel)
    : 'Basic';

const MembershipLevelMapping = {
  BASIC: 'Basic',
  PLUS: 'Plus',
  'PLUS-RV': 'Plus-RV',
  PREMIER: 'Premier',
};
